import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { useBulkCreateHoroscopeEntries } from "@/hooks/useHoroscopeEntries";
import {
  BulkCreateResponse,
  CreateHoroscopeEntryData,
} from "@/lib/api/horoscope.service";
import { HOROSCOPE_LANGUAGES as SUPPORTED_LANGUAGES } from "@/lib/studio-constants";
import {
  AlertCircle,
  CheckCircle2,
  FileJson,
  FileText,
  Search,
  TriangleAlert,
  Upload,
} from "lucide-react";
import Papa from "papaparse";
import { useMemo, useRef, useState } from "react";
import { z } from "zod";

const VALID_ZODIAC_SIGNS = [
  "aries",
  "taurus",
  "gemini",
  "cancer",
  "leo",
  "virgo",
  "libra",
  "scorpio",
  "sagittarius",
  "capricorn",
  "aquarius",
  "pisces",
];

const CSV_HEADER_TO_KEY: Record<string, string> = {
  zodiacsign: "zodiacSign",
  "zodiac sign": "zodiacSign",
  zodiac_sign: "zodiacSign",
  date: "date",
  language: "language",
  lang: "language",
  readingtext: "readingText",
  "reading text": "readingText",
  reading_text: "readingText",
  title: "title",
};

function normalizeCsvRow(
  row: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [header, value] of Object.entries(row)) {
    const normalized = header.trim().toLowerCase().replace(/\s+/g, " ");
    const key =
      CSV_HEADER_TO_KEY[normalized] ??
      CSV_HEADER_TO_KEY[normalized.replace(/\s/g, "_")] ??
      (header.trim() || header);
    out[key] = value;
  }
  return out;
}

const rawRowSchema = z.object({
  zodiacSign: z
    .string()
    .min(1, "Zodiac sign is required")
    .refine((val) => VALID_ZODIAC_SIGNS.includes(val.toLowerCase()), {
      message:
        "Invalid zodiac sign (use: aries, taurus, gemini, cancer, leo, virgo, libra, scorpio, sagittarius, capricorn, aquarius, pisces)",
    }),
  date: z
    .string()
    .min(1, "Date is required")
    .refine((val) => /^\d{4}-\d{2}-\d{2}$/.test(val), {
      message: "Invalid date format (use YYYY-MM-DD)",
    })
    .refine((val) => !isNaN(new Date(val).getTime()), {
      message: "Invalid date value",
    }),
  language: z
    .string()
    .min(2, "Language code is required")
    .max(5)
    .refine(
      (val) => SUPPORTED_LANGUAGES.some((l) => l.code === val.toLowerCase()),
      {
        message: `Unsupported language code (supported: ${SUPPORTED_LANGUAGES.map((l) => l.code).join(", ")})`,
      },
    ),
  title: z.string().optional(),
  readingText: z.string().min(1, "Reading text is required"),
});

type RawRow = z.infer<typeof rawRowSchema>;

export function BulkUploadTab() {
  const ALL_FILTER_VALUE = "__all__";
  const jsonInputRef = useRef<HTMLInputElement>(null);
  const csvInputRef = useRef<HTMLInputElement>(null);
  const [rawInput, setRawInput] = useState("");
  const [parsedEntries, setParsedEntries] = useState<
    CreateHoroscopeEntryData[]
  >([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSignFilter, setSelectedSignFilter] =
    useState(ALL_FILTER_VALUE);
  const [selectedLanguageFilter, setSelectedLanguageFilter] =
    useState(ALL_FILTER_VALUE);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [parseWarnings, setParseWarnings] = useState<string[]>([]);
  const [uploadResult, setUploadResult] = useState<BulkCreateResponse | null>(
    null,
  );
  const [selectedFormat, setSelectedFormat] = useState<"json" | "csv">("json");

  const bulkCreateMutation = useBulkCreateHoroscopeEntries();

  const resetPreviewState = () => {
    setParsedEntries([]);
    setSearchQuery("");
    setSelectedSignFilter(ALL_FILTER_VALUE);
    setSelectedLanguageFilter(ALL_FILTER_VALUE);
  };

  const availableSigns = useMemo(() => {
    return Array.from(
      new Set(parsedEntries.map((entry) => entry.zodiacSign)),
    ).sort((a, b) => a.localeCompare(b));
  }, [parsedEntries]);

  const availableLanguages = useMemo(() => {
    return Array.from(
      new Set(
        parsedEntries.flatMap((entry) =>
          entry.translations.map((translation) => translation.languageCode),
        ),
      ),
    ).sort((a, b) => a.localeCompare(b));
  }, [parsedEntries]);

  const filteredParsedEntries = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return parsedEntries.filter((entry) => {
      const matchesSign =
        selectedSignFilter === ALL_FILTER_VALUE ||
        entry.zodiacSign === selectedSignFilter;
      const matchesLanguage =
        selectedLanguageFilter === ALL_FILTER_VALUE ||
        entry.translations.some(
          (translation) => translation.languageCode === selectedLanguageFilter,
        );

      if (!matchesSign || !matchesLanguage) {
        return false;
      }

      if (!query) {
        return true;
      }

      const searchableContent = [
        entry.zodiacSign,
        entry.date,
        entry.primaryLanguage,
        ...entry.translations.map((translation) => translation.languageCode),
        ...entry.translations.map((translation) => translation.title || ""),
        ...entry.translations.map((translation) => translation.readingText),
      ]
        .join(" ")
        .toLowerCase();

      return searchableContent.includes(query);
    });
  }, [
    parsedEntries,
    searchQuery,
    selectedLanguageFilter,
    selectedSignFilter,
    ALL_FILTER_VALUE,
  ]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = reader.result as string;
      setRawInput(text);
      setParseErrors([]);
      setUploadResult(null);
      resetPreviewState();
    };
    reader.onerror = () => setParseErrors(["Failed to read file."]);
    reader.readAsText(file, "UTF-8");
    e.target.value = "";
  };

  const handleParse = () => {
    setParseErrors([]);
    setUploadResult(null);
    resetPreviewState();
    setParseWarnings([]);

    if (!rawInput.trim()) {
      setParseErrors(["Input is empty."]);
      return;
    }

    let parsedRows: any[] = [];
    const errors: string[] = [];
    const warnings: string[] = [];

    const trimmed = rawInput.trim();
    if (trimmed.startsWith("[") || trimmed.startsWith("{")) {
      try {
        const parsed = JSON.parse(rawInput);
        if (Array.isArray(parsed)) {
          parsedRows = parsed;
        } else if (parsed && Array.isArray(parsed.entries)) {
          parsedRows = parsed.entries;
        } else {
          throw new Error(
            "JSON must be an array or an object with an 'entries' array",
          );
        }
      } catch (e: any) {
        setParseErrors([`JSON Parse Error: ${e.message}`]);
        return;
      }
    } else {
      const result = Papa.parse(rawInput as string, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (header) => header.trim(),
      });

      if (result.errors.length > 0) {
        const errorMessages = result.errors.map(
          (err) => `Row ${err.row || "unknown"}: ${err.message}`,
        );
        setParseErrors(errorMessages);
        return;
      }

      const rawData = result.data as Record<string, unknown>[];
      parsedRows = rawData.map((row) => normalizeCsvRow(row));
    }

    const validRows: RawRow[] = [];
    parsedRows.forEach((row, index) => {
      const result = rawRowSchema.safeParse(row);
      if (result.success) {
        validRows.push({
          ...result.data,
          zodiacSign: result.data.zodiacSign.toLowerCase(),
          language: result.data.language.toLowerCase(),
        });
      } else {
        const messages = result.error.issues
          .map((i) => `${i.path.join(".")}: ${i.message}`)
          .join(", ");
        errors.push(`Row ${index + 1}: ${messages}`);
      }
    });

    if (errors.length > 0) {
      setParseErrors(errors);
    }

    const KEY_SEP = " ";
    const seenKeys = new Map<string, number[]>();
    validRows.forEach((row, index) => {
      const key = [row.zodiacSign, row.date, row.language].join(KEY_SEP);
      if (!seenKeys.has(key)) {
        seenKeys.set(key, []);
      }
      seenKeys.get(key)!.push(index + 1);
    });

    const dedupKeys = new Set<string>();
    seenKeys.forEach((rowNumbers, key) => {
      if (rowNumbers.length > 1) {
        const [zodiacSign, date, language] = key.split(KEY_SEP);
        warnings.push(
          `Duplicate: Rows ${rowNumbers.join(", ")} share sign (${zodiacSign}), date (${date}), and language (${language}) — keeping last occurrence.`,
        );
        dedupKeys.add(key);
      }
    });

    if (warnings.length > 0) {
      setParseWarnings(warnings);
    }

    const dedupedByKey = new Map<string, RawRow>();
    validRows.forEach((row) => {
      const key = [row.zodiacSign, row.date, row.language].join(KEY_SEP);
      dedupedByKey.set(key, row);
    });
    const dedupedRows = Array.from(dedupedByKey.values());

    const groups = new Map<string, RawRow[]>();
    dedupedRows.forEach((row) => {
      const key = [row.zodiacSign, row.date].join(KEY_SEP);
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(row);
    });

    const finalEntries: CreateHoroscopeEntryData[] = [];
    groups.forEach((rows, key) => {
      const [zodiacSign, date] = key.split(KEY_SEP);

      const hasEn = rows.some((r) => r.language === "en");
      const primaryLanguage = hasEn ? "en" : rows[0].language;

      const newEntry: CreateHoroscopeEntryData = {
        zodiacSign,
        date,
        primaryLanguage,
        translations: rows.map((r) => ({
          languageCode: r.language,
          readingText: r.readingText,
          title: r.title,
          isDefault: r.language === primaryLanguage,
        })),
      };

      finalEntries.push(newEntry);
    });

    setParsedEntries(finalEntries);
  };

  const handleUpload = () => {
    if (parsedEntries.length === 0) return;

    bulkCreateMutation.mutate(parsedEntries, {
      onSuccess: (data) => {
        setUploadResult(data);
        setRawInput("");
        resetPreviewState();
        setParseErrors([]);
        setParseWarnings([]);
      },
    });
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <FileJson className="w-4 h-4" /> JSON Format
            </CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="text-xs bg-muted p-2 rounded-md overflow-x-auto">
              {`[
  {
    "zodiacSign": "aries",
    "date": "2024-05-20",
    "language": "en",
    "readingText": "Today is a bold day...",
    "title": "Aries Daily"
  }
]`}
            </pre>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <FileText className="w-4 h-4" /> CSV Format
            </CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="text-xs bg-muted p-2 rounded-md overflow-x-auto">
              {`zodiacSign,date,language,readingText,title
aries,2024-05-20,en,"Today is bold...","Aries Daily"
taurus,2024-05-20,es,"Hoy es un día...","Tauro"
`}
            </pre>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Raw Data Input</CardTitle>
          <CardDescription>
            Select the format of your data, then paste or upload a file.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2 items-center">
            <Button
              type="button"
              variant={selectedFormat === "json" ? "default" : "outline"}
              size="sm"
              onClick={() => {
                setSelectedFormat("json");
                setUploadResult(null);
                resetPreviewState();
              }}
              className={
                selectedFormat === "json" ? "evo-gradient text-white" : ""
              }
            >
              <FileJson className="w-4 h-4 mr-2" />
              JSON
            </Button>
            <Button
              type="button"
              variant={selectedFormat === "csv" ? "default" : "outline"}
              size="sm"
              onClick={() => {
                setSelectedFormat("csv");
                setUploadResult(null);
                resetPreviewState();
              }}
              className={
                selectedFormat === "csv" ? "evo-gradient text-white" : ""
              }
            >
              <FileText className="w-4 h-4 mr-2" />
              CSV
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {selectedFormat === "json" && (
              <>
                <input
                  ref={jsonInputRef}
                  type="file"
                  accept=".json,application/json"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => jsonInputRef.current?.click()}
                >
                  <FileJson className="w-4 h-4 mr-2" />
                  Upload JSON File
                </Button>
              </>
            )}
            {selectedFormat === "csv" && (
              <>
                <input
                  ref={csvInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => csvInputRef.current?.click()}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Upload CSV File
                </Button>
              </>
            )}
          </div>
          <Textarea
            placeholder={
              selectedFormat === "json"
                ? "Paste JSON data here or upload a JSON file above..."
                : "Paste CSV data here or upload a CSV file above..."
            }
            className="min-h-[200px] font-mono text-sm"
            value={rawInput}
            onChange={(e) => setRawInput(e.target.value)}
          />
          <Button onClick={handleParse} variant="secondary">
            Parse {selectedFormat === "json" ? "JSON" : "CSV"}
          </Button>

          {parseErrors.length > 0 && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Parse Errors ({parseErrors.length})</AlertTitle>
              <AlertDescription>
                <ul className="list-disc pl-4 mt-2 text-xs max-h-[100px] overflow-y-auto">
                  {parseErrors.map((err, i) => (
                    <li key={i}>{err}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {parseWarnings.length > 0 && (
            <Alert className="border-yellow-500 text-yellow-800 bg-yellow-50">
              <TriangleAlert className="h-4 w-4 text-yellow-600" />
              <AlertTitle>Parse Warnings ({parseWarnings.length})</AlertTitle>
              <AlertDescription>
                <ul className="list-disc pl-4 mt-2 text-xs max-h-[150px] overflow-y-auto">
                  {parseWarnings.map((warn, i) => (
                    <li key={i}>{warn}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {uploadResult && (
            <Alert
              className={
                uploadResult.errors.length === 0
                  ? "border-green-500 text-green-700 bg-green-50"
                  : "border-yellow-500 text-yellow-700 bg-yellow-50"
              }
            >
              {uploadResult.errors.length === 0 ? (
                <CheckCircle2 className="h-4 w-4 text-green-600" />
              ) : (
                <AlertCircle className="h-4 w-4 text-yellow-600" />
              )}
              <AlertTitle>Upload Complete</AlertTitle>
              <AlertDescription className="mt-2 text-sm">
                <div className="flex gap-4">
                  <span>
                    <strong>Created:</strong> {uploadResult.created}
                  </span>
                  {!!uploadResult.updated && (
                    <span>
                      <strong>Languages Added:</strong> {uploadResult.updated}
                    </span>
                  )}
                  <span>
                    <strong>Skipped (Duplicates):</strong>{" "}
                    {uploadResult.skipped}
                  </span>
                  <span>
                    <strong>Errors:</strong> {uploadResult.errors.length}
                  </span>
                </div>
                {uploadResult.errors.length > 0 && (
                  <ul className="list-disc pl-4 mt-2 text-xs max-h-[100px] overflow-y-auto text-red-600">
                    {uploadResult.errors.map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                  </ul>
                )}
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {parsedEntries.length > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>
                Preview ({parsedEntries.length} grouped entries)
              </CardTitle>
              <CardDescription>
                Search and filter the grouped entries before uploading. Preview
                filters do not change what gets uploaded.
              </CardDescription>
            </div>
            <Button
              onClick={handleUpload}
              disabled={bulkCreateMutation.isPending}
              className="evo-gradient"
            >
              {!bulkCreateMutation.isPending && (
                <Upload className="w-4 h-4 ml-2" />
              )}
              {bulkCreateMutation.isPending
                ? "Uploading..."
                : "Upload as Drafts"}
            </Button>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-3 mb-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="relative w-full lg:max-w-md">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search sign, date, language, title, or reading text..."
                  className="pl-9"
                />
              </div>
              <div className="flex flex-col gap-3 sm:flex-row">
                <Select
                  value={selectedSignFilter}
                  onValueChange={setSelectedSignFilter}
                >
                  <SelectTrigger className="w-full sm:w-[180px]">
                    <SelectValue placeholder="All signs" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL_FILTER_VALUE}>All Signs</SelectItem>
                    {availableSigns.map((sign) => (
                      <SelectItem key={sign} value={sign}>
                        {sign.charAt(0).toUpperCase() + sign.slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={selectedLanguageFilter}
                  onValueChange={setSelectedLanguageFilter}
                >
                  <SelectTrigger className="w-full sm:w-[180px]">
                    <SelectValue placeholder="All languages" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL_FILTER_VALUE}>
                      All Languages
                    </SelectItem>
                    {availableLanguages.map((language) => (
                      <SelectItem key={language} value={language}>
                        {language.toUpperCase()}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mb-4 text-sm text-muted-foreground">
              {filteredParsedEntries.length} of {parsedEntries.length} grouped
              entries shown
            </div>

            {filteredParsedEntries.length === 0 ? (
              <div className="flex flex-col items-center justify-center rounded-md border border-dashed py-12 text-center">
                <Search className="mb-3 h-8 w-8 text-muted-foreground" />
                <h3 className="text-lg font-medium">No parsed entries found</h3>
                <p className="text-sm text-muted-foreground">
                  Try adjusting your search or filters to review the parsed
                  data.
                </p>
              </div>
            ) : (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Sign</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Primary Lang</TableHead>
                      <TableHead>Translations</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredParsedEntries.slice(0, 50).map((entry, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-medium capitalize">
                          {entry.zodiacSign}
                        </TableCell>
                        <TableCell>{entry.date}</TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {entry.primaryLanguage.toUpperCase()}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1 flex-wrap">
                            {entry.translations.map((t, idx) => (
                              <Badge key={idx} variant="secondary">
                                {t.languageCode.toUpperCase()}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
            {filteredParsedEntries.length > 50 && (
              <p className="text-sm text-muted-foreground mt-4 text-center">
                Showing first 50 filtered entries.{" "}
                {filteredParsedEntries.length - 50} more match the current
                search and filters.
              </p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
